import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';
import { v4 as uuidv4 } from 'uuid';
import { parser as jsParser } from '@lezer/javascript';
import { createTenantStorage } from './tenantStorage';

export type ScriptPreset = {
  id: string;
  name: string;
  script: string;
  variables: Record<string, string>;
  createdAt: number;
  updatedAt: number;
};

type ApplicationScriptsConfig = {
  presets: ScriptPreset[];
  activePresetId: string | null;
  enabled: boolean;
};

type PreRequestScriptState = {
  scriptsByApplication: Record<string, ApplicationScriptsConfig>;

  getAppConfig: (applicationId: string) => ApplicationScriptsConfig;
  getActivePreset: (applicationId: string) => ScriptPreset | undefined;

  createPreset: (
    applicationId: string,
    data: Pick<ScriptPreset, 'name' | 'script'> &
      Partial<Pick<ScriptPreset, 'variables'>>,
  ) => ScriptPreset;
  updatePreset: (
    applicationId: string,
    presetId: string,
    updates: Partial<Pick<ScriptPreset, 'name' | 'script' | 'variables'>>,
  ) => void;
  deletePreset: (applicationId: string, presetId: string) => void;
  duplicatePreset: (
    applicationId: string,
    presetId: string,
  ) => ScriptPreset | null;

  setActivePresetId: (applicationId: string, presetId: string | null) => void;
  setEnabled: (applicationId: string, enabled: boolean) => void;
  setPresetVariables: (
    applicationId: string,
    presetId: string,
    variables: Record<string, string>,
  ) => void;
};

const DEFAULT_APP_CONFIG: ApplicationScriptsConfig = {
  presets: [],
  activePresetId: null,
  enabled: false,
};

function getOrDefault(
  state: Record<string, ApplicationScriptsConfig>,
  applicationId: string,
): ApplicationScriptsConfig {
  const stored = state[applicationId];
  if (!stored) return DEFAULT_APP_CONFIG;
  return {
    presets: Array.isArray(stored.presets) ? stored.presets : [],
    activePresetId: stored.activePresetId ?? null,
    enabled: stored.enabled ?? false,
  };
}

export const usePreRequestScriptStore = create<PreRequestScriptState>()(
  persist(
    (set, get) => ({
      scriptsByApplication: {},

      getAppConfig: (applicationId) =>
        getOrDefault(get().scriptsByApplication, applicationId),

      getActivePreset: (applicationId) => {
        const config = getOrDefault(get().scriptsByApplication, applicationId);
        if (!config.activePresetId) return undefined;
        return config.presets.find((p) => p.id === config.activePresetId);
      },

      createPreset: (applicationId, data) => {
        const now = Date.now();
        const newPreset: ScriptPreset = {
          id: uuidv4(),
          name: data.name,
          script: data.script,
          variables: data.variables ?? {},
          createdAt: now,
          updatedAt: now,
        };

        set((state) => {
          const current = getOrDefault(
            state.scriptsByApplication,
            applicationId,
          );
          return {
            scriptsByApplication: {
              ...state.scriptsByApplication,
              [applicationId]: {
                ...current,
                presets: [...current.presets, newPreset],
                activePresetId: current.activePresetId || newPreset.id,
              },
            },
          };
        });

        return newPreset;
      },

      updatePreset: (applicationId, presetId, updates) => {
        set((state) => {
          const current = getOrDefault(
            state.scriptsByApplication,
            applicationId,
          );
          return {
            scriptsByApplication: {
              ...state.scriptsByApplication,
              [applicationId]: {
                ...current,
                presets: current.presets.map((p) =>
                  p.id === presetId
                    ? { ...p, ...updates, updatedAt: Date.now() }
                    : p,
                ),
              },
            },
          };
        });
      },

      deletePreset: (applicationId, presetId) => {
        set((state) => {
          const current = getOrDefault(
            state.scriptsByApplication,
            applicationId,
          );
          const remaining = current.presets.filter((p) => p.id !== presetId);
          const newActiveId =
            current.activePresetId === presetId
              ? (remaining[0]?.id ?? null)
              : current.activePresetId;

          return {
            scriptsByApplication: {
              ...state.scriptsByApplication,
              [applicationId]: {
                ...current,
                presets: remaining,
                activePresetId: newActiveId,
              },
            },
          };
        });
      },

      duplicatePreset: (applicationId, presetId) => {
        const current = getOrDefault(get().scriptsByApplication, applicationId);
        const original = current.presets.find((p) => p.id === presetId);
        if (!original) return null;

        const now = Date.now();
        const newPreset: ScriptPreset = {
          ...original,
          id: uuidv4(),
          name: `${original.name} (Copy)`,
          createdAt: now,
          updatedAt: now,
        };

        set((state) => {
          const cur = getOrDefault(state.scriptsByApplication, applicationId);
          return {
            scriptsByApplication: {
              ...state.scriptsByApplication,
              [applicationId]: {
                ...cur,
                presets: [...cur.presets, newPreset],
              },
            },
          };
        });

        return newPreset;
      },

      setActivePresetId: (applicationId, presetId) => {
        set((state) => {
          const current = getOrDefault(
            state.scriptsByApplication,
            applicationId,
          );
          return {
            scriptsByApplication: {
              ...state.scriptsByApplication,
              [applicationId]: {
                ...current,
                activePresetId: presetId,
              },
            },
          };
        });
      },

      setEnabled: (applicationId, enabled) => {
        set((state) => {
          const current = getOrDefault(
            state.scriptsByApplication,
            applicationId,
          );
          return {
            scriptsByApplication: {
              ...state.scriptsByApplication,
              [applicationId]: {
                ...current,
                enabled,
              },
            },
          };
        });
      },

      setPresetVariables: (applicationId, presetId, variables) => {
        set((state) => {
          const current = getOrDefault(
            state.scriptsByApplication,
            applicationId,
          );
          return {
            scriptsByApplication: {
              ...state.scriptsByApplication,
              [applicationId]: {
                ...current,
                presets: current.presets.map((p) =>
                  p.id === presetId
                    ? { ...p, variables, updatedAt: Date.now() }
                    : p,
                ),
              },
            },
          };
        });
      },
    }),
    {
      name: 'terra-proxy-pre-request-scripts',
      storage: createJSONStorage(() => createTenantStorage()),
      partialize: (state) => ({
        scriptsByApplication: state.scriptsByApplication,
      }),
    },
  ),
);

export const useApplicationScriptConfig = (
  applicationId: string | undefined,
) => {
  const appConfig = usePreRequestScriptStore(
    useShallow((state) =>
      applicationId ? state.scriptsByApplication[applicationId] : undefined,
    ),
  );

  const storeSetEnabled = usePreRequestScriptStore((state) => state.setEnabled);
  const storeSetActivePresetId = usePreRequestScriptStore(
    (state) => state.setActivePresetId,
  );
  const storeSetPresetVariables = usePreRequestScriptStore(
    (state) => state.setPresetVariables,
  );

  const config = appConfig || DEFAULT_APP_CONFIG;

  const activePreset = useMemo(
    () =>
      config.activePresetId
        ? config.presets.find((p) => p.id === config.activePresetId)
        : undefined,
    [config.activePresetId, config.presets],
  );

  const setEnabled = useCallback(
    (enabled: boolean) => {
      if (applicationId) storeSetEnabled(applicationId, enabled);
    },
    [applicationId, storeSetEnabled],
  );

  const setActivePresetId = useCallback(
    (presetId: string | null) => {
      if (applicationId) storeSetActivePresetId(applicationId, presetId);
    },
    [applicationId, storeSetActivePresetId],
  );

  const setActivePresetVariables = useCallback(
    (variables: Record<string, string>) => {
      if (applicationId && config.activePresetId)
        storeSetPresetVariables(
          applicationId,
          config.activePresetId,
          variables,
        );
    },
    [applicationId, config.activePresetId, storeSetPresetVariables],
  );

  return {
    presets: config.presets,
    activePreset,
    activePresetId: config.activePresetId,
    enabled: config.enabled,
    script: activePreset?.script ?? '',
    variables: activePreset?.variables ?? {},
    setEnabled,
    setActivePresetId,
    setActivePresetVariables,
  };
};

export function validateScriptSyntax(script: string): {
  valid: boolean;
  error?: string;
} {
  if (!script.trim()) return { valid: true };

  const tree = jsParser.parse(script);
  let errorMessage: string | undefined;

  tree.iterate({
    enter(node) {
      if (node.type.isError && !errorMessage) {
        const position = node.from;
        const lines = script.slice(0, position).split('\n');
        const line = lines.length;
        const col = (lines[lines.length - 1]?.length ?? 0) + 1;
        errorMessage = `Syntax error at line ${line}, col ${col}`;
        return false;
      }
    },
  });

  return errorMessage ? { valid: false, error: errorMessage } : { valid: true };
}
